import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import firebase from '../../config/firebase.config';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import styles from './AddTask.module.scss';
// custom  imports
import './taskpage.css';
import { startCase } from 'lodash';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FirebaseImageUploadPlugin } from './UploadPlugin';
import AlertNotification from '../Tasks/AlertNotification';
import { useTeamMemberDetailsForSelect } from '../../hooks/useTeamMemberDetailsForSelect';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { priorityOptions, statusOptions } from './AddTask.constants';
import { Button, buttonVariants } from '@/components/Button';

export const AddTaskComponent = () => {
  const database = firebase.firestore();
  const dispatch = useDispatch();

  const imageUpload = useSelector((state) => ({
    imageUpload: state.taskImage.isLoading,
  }));

  const { activeCompany } = useSelector((state) => state.auth.profile);

  const teams = useSelector((state) => ({
    teams: state.team.teams,
    loadingProject: state.team.loadingProject,
    activeTeam: state.team.activeTeam,
    selectedTeamId: state.team.selectedTeamID,
    projects: state.team.projects,
    tasks: state.team.tasks,
    activeUserId: state.team.activeUserID,
    all: state.team.all,
    loading: false,
    selectedProject: state.team.selectedProject,
    active: state.team.active,
    membersData: state.team.membersData,
  }));

  const activeTeamId =
    teams.active?.activeTeamid ?? teams?.selectedTeamId ?? null;
  const { memberDetails } = useTeamMemberDetailsForSelect(activeTeamId);

  const members = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .map(([uid, m]) => ({
        userUID: uid,
        userName: m?.userName || m?.userEmailid || uid,
      }))
      .filter((m) => m.userUID)
      .sort((a, b) => (a.userName || '').localeCompare(b.userName || ''));
  }, [memberDetails]);

  const [selectedDate, setSelectedDate] = useState(new Date());

  const [formValues, setFormValues] = useState({
    title: null,
    summary: null,
    assignee: null,
    status: null,
    priority: null,
    project: null,
    date: { value: new Date() },
  });

  const teamData = [];
  if (teams.projects.length > 0) {
    teams.projects.map((item) => {
      if (!item.data.deleted) {
        const obj = {
          name: item.data.project_name,
          id: item.id,
        };
        teamData.push(obj);
      }
    });
  }

  const newTeamData = teamData?.map((item) => ({
    value: item?.id,
    label: item.name,
  }));
  const assigneeDropdownOptions = useMemo(
    () =>
      (members || []).map((member) => ({
        value: member.userUID,
        label: member.userName,
      })),
    [members]
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successData, setSuccessData] = useState(false);
  const [deleteData, setDeleteData] = useState(false);
  const [errorData, setErrorData] = useState(false);

  const pad2 = (n) => {
    return (n < 10 ? '0' : '') + n;
  };
  const handleSubmit = async () => {
    if (
      !formValues.project?.value ||
      !formValues.title?.value ||
      !formValues.summary?.value ||
      !formValues.status?.value ||
      !formValues.priority?.value ||
      !formValues.assignee?.value
    ) {
      const requiredField = [
        'project',
        'title',
        'summary',
        'assignee',
        'status',
        'priority',
      ];
      for (let i = 0; i < requiredField.length; i++) {
        const field = requiredField[i];
        if (!formValues[field]?.value) {
          addNotification({
            message: `${startCase(field)} is required`,
            level: notificationStates.error,
          });
          return false;
        }
      }
    }
    setSubmitLoading(true);
    var month = pad2(formValues.date?.value?.getMonth() + 1); //months (0-11)
    var day = pad2(formValues.date?.value?.getDate()); //day (1-31)
    var year = formValues.date?.value?.getFullYear();
    let dateId = day + '/' + month + '/' + year;

    const taskRef = database.collection('taskDatabase').doc();
    database
      .runTransaction(async (transaction) => {
        const cityRef = database
          .collection('companyDatabase')
          .doc(activeCompany.companyId)
          .collection('projects')
          .doc(formValues.project?.value);
        const doc = await transaction.get(cityRef);

        const project = doc.data();
        const arr = [];
        arr.push(taskRef.id);

        if (project.taskList) {
          project.taskList.map((item) => arr.push(item));
          const newA = project.taskList.filter(
            (item) =>
              item.toLowerCase() === formValues.title?.value?.toLowerCase()
          );
          if (newA.length === 0) {
            const cityRef = database
              .collection('companyDatabase')
              .doc(activeCompany.companyId)
              .collection('projects')
              .doc(formValues.project?.value);
            await transaction.update(cityRef, { taskList: arr });
          }
        } else {
          const cityRef = database
            .collection('companyDatabase')
            .doc(activeCompany.companyId)
            .collection('projects')
            .doc(formValues.project?.value);
          await transaction.update(cityRef, { taskList: [taskRef.id] });
        }
        transaction.set(taskRef, {
          Title: formValues.title?.value,
          Description: formValues.summary?.value,
          Priority: formValues.priority?.value,
          Assignee: formValues.assignee?.value,
          Status: formValues.status?.value,
          ProjectID: formValues.project?.value,
          ETA: dateId,
        });
      })
      .then(async (data) => {
        setFormValues({
          title: { value: '' },
          summary: { value: '' },
          assignee: null,
          status: null,
          priority: null,
          project: null,
          date: { value: new Date() },
        });
        setSuccessData(true);
        setSubmitLoading(false);
        addNotification({
          message: 'Task submitted successfully',
          level: notificationStates.success,
        });
      })
      .catch((error) => {
        addNotification({
          message: 'Task submission failed',
          level: notificationStates.error,
        });
      });
  };

  const task = [];
  const findUser = assigneeDropdownOptions?.filter(
    (item) => item.value === task[0]?.Assignee
  );

  useEffect(() => {
    if (findUser.length > 0) {
      setFormValues({ ...formValues, assignee: findUser[0] });
    }
  }, [findUser.length > 0]);

  useEffect(() => {
    setTimeout(() => {
      setSuccessData(false);
      setErrorData(false);
      setDeleteData(false);
    }, 1500);
  }, [successData, errorData, deleteData]);

  const dispatchRef = useRef();
  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  const FileUploadPlugin = useCallback(
    function (editor) {
      return FirebaseImageUploadPlugin(
        editor,
        formValues.title?.value,
        dispatchRef
      );
    },
    [formValues, dispatchRef]
  );
  return (
    <>
      <div className={styles.addTaskPage}>
        <div className={styles.addTaskFormContainer}>
          <div className={styles.addTaskForm}>
            <Dropdown
              options={newTeamData}
              placeholder="Select Project"
              selectStyles={borderedSelectStyles}
              onChange={(option) => {
                setFormValues({ ...formValues, project: option });
              }}
              value={formValues.project}
              label={'Project *'}
            />
            <Input
              label="Title *"
              placeholder="Task Title"
              value={formValues.title?.value}
              onChange={(e) => {
                setFormValues({
                  ...formValues,
                  title: { value: e.target.value },
                });
              }}
            />
            <div className={styles.descriptionEditor}>
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
              >
                Summary *
              </Text>
              <CKEditor
                editor={ClassicEditor}
                data={formValues.summary?.value}
                config={{
                  extraPlugins: [FileUploadPlugin],
                }}
                onChange={(event, editor) => {
                  const value = editor.getData();
                  setFormValues({ ...formValues, summary: { value } });
                }}
              />
            </div>
            <div className={styles.createTaskButton}>
              <Button
                text="Create Task"
                variant={buttonVariants.primary}
                onClick={handleSubmit}
                disabled={imageUpload.imageUpload}
                loading={submitLoading}
              />
            </div>
          </div>
          <div className={styles.addTaskFormDetails}>
            <Text
              variant={textVariantsEnum.Header}
              fontWeight={fontWeightEnum.Bold}
            >
              Details
            </Text>
            <div className={styles.addTaskFormDetailsContent}>
              <Dropdown
                options={assigneeDropdownOptions}
                selectStyles={borderedSelectStyles}
                placeholder="Select assignee"
                onChange={(option) =>
                  setFormValues({ ...formValues, assignee: option })
                }
                label="Assignee *"
              />
              <Dropdown
                options={statusOptions}
                selectStyles={borderedSelectStyles}
                placeholder="Select Status"
                onChange={(option) =>
                  setFormValues({ ...formValues, status: option })
                }
                label="Status *"
              />
              <Dropdown
                options={priorityOptions}
                selectStyles={borderedSelectStyles}
                placeholder="Select Priority"
                onChange={(option) =>
                  setFormValues({ ...formValues, priority: option })
                }
                label="Priority *"
              />
              <div className={styles.datePicker}>
                <Text
                  variant={textVariantsEnum.Text_13}
                  fontWeight={fontWeightEnum.Normal}
                >
                  ETA
                </Text>
                <DatePicker
                  className={styles.datePickerInput}
                  selected={selectedDate}
                  onChange={(date) => {
                    setSelectedDate(date);
                    setFormValues({
                      ...formValues,
                      date: { value: date },
                    });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
